import Icon from "@ant-design/icons";
import {
  ApiOutlined,
  AuditOutlined,
  BankOutlined,
  CalendarOutlined,
  CommentOutlined,
  ContactsOutlined,
  DollarOutlined,
  FundOutlined,
  InfoCircleOutlined,
  RiseOutlined,
  RobotOutlined,
  SettingOutlined,
  TagsOutlined,
  VideoCameraOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  ConfigProvider,
  Dropdown,
  Flex,
  Layout,
  Menu,
  Result,
  Spin,
  Tooltip,
  Tour,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BranchSelector from "../components/BranchSelector";
import ChatWidget from "../components/ChatWidget";
import { useAuth } from "../context/AuthContext";
import useCameras from "../hooks/useCameras";
import useZones from "../hooks/useZones";
import { MENU_ITEMS_DESCRIPTION } from "../utils/constants";
import useApi from "../utils/useApi";
import { podeVerGateway } from "./gateway/acesso";

const { Header, Content, Sider } = Layout;

// Largura da sidebar aberta. O Content usa o mesmo valor como marginLeft: antes
// eram 230 contra 220, e a sidebar (fixed, zIndex 1000) cobria 10px do conteúdo.
const SIDER_WIDTH = 248;

function getIcon(src, disable = false) {
  return (
    <Icon
      component={() => (
        <img
          src={src}
          style={{ width: "20px", height: "20px", opacity: disable ? 0.5 : 1 }}
          alt=""
        />
      )}
    />
  );
}

function getOutlinedIcon(Component, disable = false) {
  return (
    <Component
      style={{
        fontSize: 18,
        color: "rgba(0, 0, 0, 0.82)",
        opacity: disable ? 0.5 : 1,
      }}
    />
  );
}

function getItem(label, key, icon, children, type, disabled) {
  return {
    key,
    icon,
    children,
    label,
    type,
    disabled,
  };
}

// O texto encolhe e reticencia; o ícone nunca. Sem isto, um label longo empurra
// o (i) para fora do .ant-menu-title-content, que tem overflow hidden, e o ícone
// some — foi o que acontecia com "Análise de eventos" e "Promoções por SKU".
function getLabelWithInfo(text, descriptionKey, ref) {
  return (
    <div
      ref={ref}
      style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}>
        {text}
      </span>
      <Tooltip title={MENU_ITEMS_DESCRIPTION[descriptionKey]}>
        <InfoCircleOutlined
          style={{
            flex: "0 0 auto",
            marginLeft: 8,
            color: "gray",
            fontSize: 14,
          }}
        />
      </Tooltip>
    </div>
  );
}

function MenuBar({ children, menu_item_selected }) {
  const [collapsed, setCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [siderHeight, setSiderHeight] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [contentMargin, setContentMargin] = useState(0);
  const tutorialRef1 = useRef(null);
  const {
    user,
    logout,
    shouldShowTutorialBool,
    isZeroPhaseTutorialOpen,
    setIsZeroPhaseTutorialOpen,
  } = useAuth();
  const isAdmin = user?.id_admin || user?.is_admin;
  const isClientAdmin = Boolean(user?.is_client_admin);
  const isAdministrator = Boolean(user?.is_superuser);
  const navigate = useNavigate();
  const location = useLocation();

  const { camerasCount, isLoading: isLoadingCameras } = useCameras();
  const { data, isLoading: isLoadingZones } = useZones({
    active_only: true,
  });

  const zonesCount = data?.length;

  const hasCameras = camerasCount && camerasCount > 0;
  const hasZones = zonesCount && zonesCount > 0;

  const shouldShow =
    !isLoadingCameras && !isLoadingZones && hasCameras && hasZones;

  const onClick = (e) => {
    if (e.key === "g3_sub1") {
      window.open("https://api.whatsapp.com/send/?phone=5549936180668");
    } else if (e.key === "g3_sub2") {
      return;
    } else {
      navigate("/" + e.key);
    }
  };

  const steps = [
    {
      title: "1º passo: Abrir menu de câmeras e zonas",
      description:
        "No menu de câmeras e zonas é possível adicionar câmeras e posteriormente criar zonas.",
      nextButtonProps: {
        children: "Próximo",
        onClick: () => navigate("/zones_manager"),
      },
      target: () => tutorialRef1.current,
    },
  ];
  const { getTestConnection } = useApi();
  const {
    isSuccess: isTestConnectionSuccess,
    isLoading: isTestConnectionLoading,
    error: testConnectionError,
  } = useQuery({
    queryKey: ["test_connection"],
    queryFn: () => getTestConnection(),
    retry: false,
  });

  useEffect(() => {
    if (
      testConnectionError?.response?.status === 401 ||
      testConnectionError?.response?.status === 403
    ) {
      logout();
    }
  }, [logout, testConnectionError]);

  useEffect(() => {
    const HEADER_HEIGHT = 72;
    const CONTENT_MARGIN_PADDING = 0;
    const MOBILE_BREAKPOINT = 768;
    const COLLAPSE_BREAKPOINT = 1500;

    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      setHeaderHeight(HEADER_HEIGHT);
      setContentMargin(HEADER_HEIGHT + CONTENT_MARGIN_PADDING);
      setSiderHeight(window.innerHeight - HEADER_HEIGHT);

      if (mobile) {
        setCollapsed(true);
      } else if (window.innerWidth <= COLLAPSE_BREAKPOINT) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const items = [
    ...(isAdministrator || isClientAdmin || isAdmin
      ? [
          getItem(
            "Home",
            "home",
            getOutlinedIcon(BankOutlined),
            null,
            "item",
            false
          ),
        ]
      : []),
    ...(isAdmin || isClientAdmin || isAdministrator
      ? [
          getItem(
            "Lista da Vez",
            "lost_sales",
            getOutlinedIcon(DollarOutlined),
            null,
            "item"
          ),
        ]
      : []),
    getItem(
      "Em Alta",
      "em_alta",
      getOutlinedIcon(RiseOutlined),
      null,
      "item",
      false
    ),
    getItem(
      "Comparativo",
      "comparativo",
      getOutlinedIcon(FundOutlined),
      null,
      "item",
      false
    ),
    { type: "divider" },
    getItem(
      getLabelWithInfo("Faturamento", "revenue"),
      "revenue",
      getOutlinedIcon(DollarOutlined, !shouldShow),
      null,
      "item",
      !shouldShow
    ),
    getItem(
      getLabelWithInfo("Análise de eventos", "collections_change"),
      "collections_change",
      getOutlinedIcon(CalendarOutlined, !shouldShow),
      null,
      "item",
      !shouldShow
    ),
    getItem(
      getLabelWithInfo("Promoções por SKU", "promocoes_por_sku"),
      "promocoes_por_sku",
      getOutlinedIcon(TagsOutlined, !shouldShow),
      null,
      "item",
      !shouldShow
    ),
    getItem(
      getLabelWithInfo("Fluxo de pessoas", "flow_detections"),
      "flow_detections",
      getIcon("icons/people.png", !shouldShow),
      null,
      "item",
      !shouldShow
    ),
    getItem(
      getLabelWithInfo("Horários de pico", "peak_hours"),
      "peak_hours",
      getIcon("icons/graph.png", !shouldShow),
      null,
      "item",
      !shouldShow
    ),
    { type: "divider" },
    getItem(
      getLabelWithInfo("Gerenciar eventos", "events_manager"),
      "events_manager",
      getIcon("icons/pencil.png", !shouldShow),
      null,
      "item",
      !shouldShow
    ),
    getItem(
      getLabelWithInfo("Câmeras e Zonas", "zones_manager", tutorialRef1),
      "zones_manager",
      getIcon("icons/size.png"),
      null,
      "item"
    ),
    getItem(
      getLabelWithInfo("Dados Loja", "opening_hours"),
      "opening_hours",
      getOutlinedIcon(SettingOutlined),
      null,
      "item"
    ),
    { type: "divider" },
    ...(isAdmin || isClientAdmin || isAdministrator
      ? [
          getItem(
            getLabelWithInfo("Painel de Controle", "control_panel"),
            "control_panel",
            getIcon("icons/painel.png"),
            null,
            "item"
          ),
        ]
      : []),
    ...(isAdministrator
      ? [
          getItem(
            "Status Câmeras",
            "cameras_monitor",
            getOutlinedIcon(VideoCameraOutlined),
            null,
            "item"
          ),
          getItem(
            "Log de Acessos",
            "access_log",
            getOutlinedIcon(AuditOutlined),
            null,
            "item"
          ),
          getItem(
            "Benchmarks rSCORE",
            "rscore_benchmarks",
            getOutlinedIcon(SettingOutlined),
            null,
            "item"
          ),
          getItem(
            "Relatórios por IA",
            "ai_reports",
            getOutlinedIcon(RobotOutlined),
            null,
            "item"
          ),
          getItem(
            "Agente de IA",
            "ai_agent",
            getOutlinedIcon(CommentOutlined),
            null,
            "item"
          ),
          getItem(
            "Contatos",
            "contatos",
            getOutlinedIcon(ContactsOutlined),
            null,
            "item"
          ),
        ]
      : []),
    ...(podeVerGateway(user)
      ? [
          getItem(
            "Conexão câmeras gateway",
            "gateway_cameras",
            getOutlinedIcon(ApiOutlined),
            null,
            "item"
          ),
        ]
      : []),
  ];

  const top_items = [{ label: "" }];

  const help_items = [
    {
      label: "Fale conosco",
      key: "g3_sub1",
      icon: <WhatsAppOutlined style={{ color: "#25D366", fontSize: "20px" }} />,
      type: "item",
    },
    {
      label: "Sair",
      key: "g3_sub2",
      icon: getIcon("icons/logout.png"),
      type: "item",
      onClick: () => logout(),
    },
  ];

  const avatarMenuItems = [
    {
      key: "logout",
      label: "Sair",
      icon: getIcon("icons/logout.png"),
      onClick: () => {
        logout();
      },
    },
  ];

  return (
    <>
      {isTestConnectionLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}>
          <Spin size="large" />
        </div>
      ) : isTestConnectionSuccess ? (
        <ConfigProvider
          theme={{
            components: {
              Menu: {
                iconSize: "20px",
                itemMarginBlock: "15px",
                groupTitleColor: "black",
                itemBg: "transparent",
                itemHoverBg: "rgba(60, 72, 88, 0.04)",
                itemSelectedBg: "#e8edf3",
                itemSelectedColor: "black",
                itemColor: "rgba(0, 0, 0, 0.88)",
                horizontalItemHoverColor: null,
                horizontalItemSelectedColor: null,
              },
              Layout: {
                siderBg: "rgba(248, 250, 252, 0.86)",
                colorBgLayout: "#f6f8fb",
              },
            },
          }}>
          <Layout hasSider className="ouran-app-shell">
            {isMobile && !collapsed && (
              <div
                className="ouran-sidebar-backdrop"
                onClick={() => setCollapsed(true)}
              />
            )}
            <Sider
              trigger={null}
              collapsible
              collapsed={collapsed}
              collapsedWidth={isMobile ? 0 : 80}
              className="ouran-app-sider"
              style={{
                height: `calc(${siderHeight}px - var(--ouran-env-banner-height, 0px))`,
                position: "fixed",
                left: 0,
                top: `calc(${headerHeight}px + var(--ouran-env-banner-height, 0px))`,
                bottom: 0,
                zIndex: 1000,
              }}
              width={SIDER_WIDTH}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}>
                <Menu
                  style={{
                    border: "0px",
                    width: "100%",
                    flex: 1,
                    overflowY: "auto",
                    scrollbarGutter: "stable",
                  }}
                  defaultSelectedKeys={[menu_item_selected]}
                  mode="inline"
                  onClick={onClick}
                  align={collapsed ? "center" : "left"}
                  items={items}
                />
                <Menu
                  style={{ border: "0px", width: "100%" }}
                  mode="inline"
                  onClick={onClick}
                  align={collapsed ? "center" : "left"}
                  items={help_items}
                  selectedKeys={[]}
                />
              </div>
            </Sider>
            <Layout>
              <Header
                style={{
                  padding: 0,
                  position: "fixed",
                  zIndex: 1001,
                  left: 0,
                  top: "var(--ouran-env-banner-height, 0px)",
                  right: 0,
                  height: headerHeight,
                  lineHeight: `${headerHeight}px`,
                }}
                className="header ouran-app-header">
                <Flex
                  style={{
                    display: "flex",
                    justifyContent: "left",
                    alignItems: "center",
                    width: "100%",
                    height: "100%",
                    padding: "0 10px 0 10px",
                    zIndex: 0,
                    gap: 2,
                  }}
                  items={top_items}>
                  <Button
                    type="text"
                    icon={getIcon("icons/menu.png")}
                    className="ouran-shell-menu-button"
                    style={{
                      background: !collapsed
                        ? "rgba(60, 72, 88, 0.05)"
                        : "transparent",
                    }}
                    onClick={() => {
                      setCollapsed((prev) => !prev);
                    }}
                  />
                  <Icon
                    component={() => (
                      <img
                        src={"icons/logo_horizontal.svg"}
                        alt="logo"
                        className="ouran-header-logo"
                        style={{
                          width: "194px",
                          height: 44,
                          objectFit: "contain",
                          transform: "translateY(-2px)",
                        }}
                      />
                    )}
                    style={{
                      display: "flex",
                      justifyContent: "left",
                      flex: "0 0 auto",
                      marginLeft: -14,
                    }}
                  />
                  <div style={{ flex: 1 }} />
                  <BranchSelector />
                  <Dropdown
                    menu={{ items: avatarMenuItems }}
                    trigger={["click"]}
                    placement="bottom"
                    arrow={{ pointAtCenter: true }}>
                    <Avatar
                      style={{
                        marginLeft: "10px",
                        marginRight: "10px",
                        cursor: "pointer",
                      }}
                      shape="square"
                      size={{ xs: 24, sm: 32, md: 40 }}>
                      {user.first_name[0].toUpperCase() +
                        user.last_name[0].toUpperCase()}
                    </Avatar>
                  </Dropdown>
                </Flex>
              </Header>
              <Content
                style={{
                  marginTop: `calc(${contentMargin}px + var(--ouran-env-banner-height, 0px))`,
                  marginLeft: isMobile ? 0 : collapsed ? 80 : SIDER_WIDTH,
                  overflow: "initial",
                  paddingTop: 24,
                  boxSizing: "border-box",
                }}
                className="ouran-app-content"
                width="100%">
                {children}
                <ChatWidget />
              </Content>
            </Layout>
          </Layout>
        </ConfigProvider>
      ) : (
        <Result
          status="500"
          title={"Erro: Não foi possível conectar"}
          subTitle="Verifique sua conexão com a internet e tente novamente."
        />
      )}
      <Tour
        open={
          shouldShowTutorialBool &&
          location.pathname === "/em_alta" &&
          isZeroPhaseTutorialOpen
        }
        onClose={() => setIsZeroPhaseTutorialOpen(false)}
        steps={steps}
        gap={{
          offset: [45, 0],
          radius: 10,
        }}
      />
    </>
  );
}

export default MenuBar;
